import { Sites } from '@ps/platform-message-framework/common/Sites';

import type { WidgetEffectParams } from '@swf/core/store';
import { sportsWidgetPollingBehavior } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import getErrorStatusCode from '@swf/sports/utils/getErrorStatusCode';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';

import type { SportsNextRacesWidgetConfig } from '../config';

import type { NextRacesHandlerResponseBody, WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<SportsNextRacesWidgetConfig>) {
    const { dispatch, select, widget, rootState } = params;
    const { clear, nextRacesRequested }: WidgetDispatch = dispatch[widget.uid];
    const widgetSelect: WidgetSelect = select[widget.uid];

    sportsWidgetPollingBehavior(params).subscribe({
      next: () => nextRacesRequested(),
      complete: clear
    });

    const hasPreloaded = widgetSelect.hasPreloaded(rootState);

    if (!hasPreloaded) {
      await nextRacesRequested();
    }
  },
  async nextRacesRequested(
    _payload: undefined,
    params: WidgetEffectParams<SportsNextRacesWidgetConfig>
  ): Promise<void> {
    const { dispatch, request, widget, select, rootState } = params;
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const { eventTypeId: configuredEventTypeId } = widget;
    const { eventTypeId: urlParamEventTypeId } = select.sports.getUrlParams(rootState);
    const sportsRequest = buildSportsRequest(request, rootState);

    if (!configuredEventTypeId && !urlParamEventTypeId) return;

    const eventTypeId = configuredEventTypeId ?? urlParamEventTypeId;

    try {
      const {
        body: { data }
      } = await sportsRequest<{ data: NextRacesHandlerResponseBody }>({
        path: `/next-races/${eventTypeId}`,
        signal: getWidgetCancellationSignal(params)
      });
      widgetDispatch.dataLoadSuccess(data);
    } catch (error) {
      if (hasRequestAborted(error)) return;
      widgetDispatch.dataLoadFailed(getErrorStatusCode(error));
    }
  },
  async dataLoadSuccess(
    _payload: undefined,
    { dispatch, select, widget, rootState }: WidgetEffectParams<SportsNextRacesWidgetConfig>
  ): Promise<void> {
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const widgetSelect: WidgetSelect = select[widget.uid];
    const { eventTypeId: configuredEventTypeId } = widget;
    const { eventTypeId: urlParamEventTypeId } = select.sports.getUrlParams(rootState);
    const site = select.cms.getSiteId(rootState);

    if (!configuredEventTypeId && !urlParamEventTypeId) return;

    const eventTypeId = configuredEventTypeId ?? urlParamEventTypeId;

    const templates = widgetSelect.templates(rootState);
    const ukIeTemplates = widgetSelect.ukIeTemplates(rootState);
    const currentTabTemplates =
      site === Sites.UK && ukIeTemplates.length ? ukIeTemplates : templates;
    const selectedTemplate = widgetSelect.selectedTemplate(rootState);

    if (!selectedTemplate && currentTabTemplates.length) {
      widgetDispatch.setSelected({ ...currentTabTemplates[0], eventTypeId: Number(eventTypeId) });
    }
  }
};
